import { useMemo, useEffect, useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type { HealthcareService, Organization } from "@medplum/fhirtypes";
import { HEALTHCARE_SERVICE_SIBLINGS_COUNT } from "@benchmark/core/constants";

/**
 * Result type for useSiblingHealthcareServices hook.
 * @agentic @benchmark/core
 */
export type SiblingServicesResult = {
  siblings: HealthcareService[];
  isLoading: boolean;
  error: Error | null;
  organizationName: string | undefined;
};

/**
 * Fetches sibling HealthcareServices (same org, excluding current) and organization name.
 * @agentic @benchmark/core
 */
export function useSiblingHealthcareServices(
  organizationId: string | undefined,
  currentServiceId: string | undefined,
): SiblingServicesResult {
  const medplum = useMedplum();
  const [services, setServices] = useState<HealthcareService[]>([]);
  const [organization, setOrganization] = useState<Organization | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSiblings = useCallback(async () => {
    if (!organizationId || !currentServiceId) {
      setServices([]);
      setOrganization(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [fetchedServices, fetchedOrg] = await Promise.all([
        medplum.searchResources("HealthcareService", {
          organization: `Organization/${organizationId}`,
          _count: String(HEALTHCARE_SERVICE_SIBLINGS_COUNT),
        }),
        medplum.readResource("Organization", organizationId),
      ]);

      setServices(fetchedServices);
      setOrganization(fetchedOrg);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setServices([]);
      setOrganization(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, organizationId, currentServiceId]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchSiblings();
    });
  }, [fetchSiblings]);

  const siblings = useMemo(() => {
    if (!services || !currentServiceId) return [];
    return services.filter((svc) => svc.id !== currentServiceId);
  }, [services, currentServiceId]);

  return {
    siblings,
    isLoading,
    error,
    organizationName: organization?.name,
  };
}
